import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  DatePicker,
  Segmented,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import Plot, { Figure } from "react-plotly.js";
import type { Annotations, Data, Shape } from "plotly.js";
import type {
  ForecasterSpec,
  ImbalanceForecastMetricsReport,
  MinuteErrorRow,
  PerMinuteForecasterVersion,
  PlannerSetupSpec,
  QuantileCalibrationRow,
  SetupValueSummary,
  StepErrorRow,
  ValueGroup,
  VersionMarker,
} from "../../../services/client";
import { getImbalanceForecastMetricsOptions } from "../../../services/client/@tanstack/react-query.gen";
import NoData from "../../NewSite/Components/OptimizationConfigurationForm/NoData";
import { DATE_FORMAT } from "../../../util/formatDate";
import { ONE_MINUTE } from "../../../util/time";
import { adminAuthHeaders } from "../adminAuthHeaders";
import { GRID_COLOR, NOW_COLOR, SURFACE_COLOR } from "./imbalanceChartLines";
import {
  BASELINE_COLOR,
  CYCLING_COSTS,
  type CyclingCost,
  ERROR_METRIC_LABELS,
  type ErrorMetric,
  type MetricsSource,
  PER_MINUTE_VERSION_LABELS,
  STEP_VERSION_LABELS,
  defaultSetups,
  euros,
  forecasterColor,
  groupAt,
  groupVersionLabel,
  labelOf,
  percent,
  perMwYear,
  setupColor,
  setupIn,
  signedEuros,
} from "./forecastMetrics";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const API_DAY_FORMAT = "YYYY-MM-DD";
// live: the per-minute forecasts are stored from 16 Oct 2026 (they expire
// after 30 days); research: the replay covers validation to the final test
const DEFAULT_RANGES: Record<MetricsSource, () => [Dayjs, Dayjs]> = {
  live: () => [dayjs("2026-10-16"), dayjs().subtract(1, "day")],
  research: () => [dayjs("2025-07-01"), dayjs("2026-10-15")],
};
const PROMINENT_STEPS = 8;
const SHOWN_STEPS = 24;
const TABLE_MINUTES = [0, 3, 7, 11, 14];
const VERSION_DASHES = ["solid", "dot", "dash", "dashdot"] as const;

const plotConfig = { displaylogo: false, responsive: true };

const markerShapes = (
  markers: VersionMarker[],
): { shapes: Partial<Shape>[]; annotations: Partial<Annotations>[] } => ({
  shapes: markers.map((marker) => ({
    type: "line",
    x0: marker.day,
    x1: marker.day,
    yref: "paper",
    y0: 0,
    y1: 1,
    line: { color: NOW_COLOR, width: 1, dash: "dot" },
  })),
  annotations: markers.map((marker, index) => ({
    x: marker.day,
    yref: "paper",
    y: 1.04 + (index % 2) * 0.09,
    text: marker.label,
    showarrow: false,
    xanchor: "left",
    font: { color: NOW_COLOR, size: 11 },
  })),
});

const clearOfZero = (summary: SetupValueSummary) => {
  const difference = summary.difference_to_baseline;
  return (
    difference !== null &&
    difference !== undefined &&
    (difference.ci05 > 0 || difference.ci95 < 0)
  );
};

type ValueTableRow = SetupValueSummary & { key: string };

const valueColumns = (
  setups: PlannerSetupSpec[],
): ColumnsType<ValueTableRow> => [
  {
    title: "Planner setup",
    dataIndex: "setup",
    render: (name: string) => {
      const setup = setups.find((candidate) => candidate.name === name);
      return (
        <Tooltip title={setup?.description}>
          <span style={{ color: setupColor(setups, name) }}>■ </span>
          {labelOf(setups, name)}
        </Tooltip>
      );
    },
  },
  {
    title: "€/MW-year",
    dataIndex: "per_mw_year",
    align: "right",
    sorter: (a, b) => a.per_mw_year - b.per_mw_year,
    defaultSortOrder: "descend",
    render: (value: number) => euros(value),
  },
  {
    title: "Net €",
    dataIndex: "net_revenue",
    align: "right",
    render: (value: number) => euros(value),
  },
  {
    title: "Minus baseline, €/MW-year (90% interval)",
    align: "right",
    render: (_, row) => {
      const difference = row.difference_to_baseline;
      if (!difference) {
        return "–";
      }
      return (
        <span>
          <Text strong>
            {signedEuros(perMwYear(difference.difference, difference.days))}
          </Text>{" "}
          <Text type="secondary">
            [{euros(perMwYear(difference.ci05, difference.days))},{" "}
            {euros(perMwYear(difference.ci95, difference.days))}]
          </Text>{" "}
          <Tag color={clearOfZero(row) ? "green" : "orange"}>
            {clearOfZero(row) ? "clear of zero" : "includes zero"}
          </Tag>
        </span>
      );
    },
  },
  {
    title: "Share of ceiling",
    dataIndex: "share_of_ceiling",
    align: "right",
    render: (value: number | null) => percent(value),
  },
  {
    title: "Full cycles / day",
    dataIndex: "cycles_per_day",
    align: "right",
    render: (value: number) => value.toFixed(2),
  },
  {
    title: "MWh moved",
    dataIndex: "mwh_moved",
    align: "right",
    render: (value: number) => euros(value),
  },
  { title: "Days", dataIndex: "days", align: "right" },
];

const ValueTable = ({
  groups,
  setups,
}: {
  groups: ValueGroup[];
  setups: PlannerSetupSpec[];
}) => (
  <div className="space-y-4">
    {groups.map((group) => (
      <div
        key={`${group.label}-${group.per_minute_version}-${group.step_version}`}
      >
        <Text type="secondary">
          {group.first_day} to {group.last_day} ({group.days} days),{" "}
          {groupVersionLabel(group)}
          {group.provisional_days > 0 &&
            `; ${group.provisional_days} provisional day(s) still waiting for settled prices`}
          .
        </Text>
        <Table<ValueTableRow>
          size="small"
          pagination={false}
          scroll={{ x: true }}
          dataSource={group.setups.map((summary) => ({
            ...summary,
            key: summary.setup,
          }))}
          columns={valueColumns(setups)}
        />
      </div>
    ))}
  </div>
);

const ROLE_TAGS: Record<
  PlannerSetupSpec["role"],
  { label: string; color: string }
> = {
  baseline: { label: "baseline", color: "default" },
  ceiling: { label: "ceiling", color: "green" },
  ours: { label: "production", color: "blue" },
  candidate: { label: "candidate", color: "purple" },
};

// The planner's price streams besides the named forecasters (see SETTLED and
// DAY_AHEAD in the backend's imbalance_forecast_metrics.py).
const priceSource = (forecasters: ForecasterSpec[], name: string) => {
  if (name === "settled") {
    return "Settled price";
  }
  if (name === "day_ahead") {
    return "Day-ahead price";
  }
  const forecaster = forecasters.find((candidate) => candidate.name === name);
  if (!forecaster) {
    return name;
  }
  return (
    <Tooltip title={forecaster.description}>
      {forecaster.kind === "step"
        ? `${forecaster.label}, pulled toward day-ahead by 0.9 per quarter hour ahead`
        : forecaster.label}
    </Tooltip>
  );
};

type SetupDefinitionRow = PlannerSetupSpec & { key: string };

const SetupDefinitions = ({
  setups,
  forecasters,
}: {
  setups: PlannerSetupSpec[];
  forecasters: ForecasterSpec[];
}) => (
  <div className="space-y-2">
    <Title level={5} className="!mb-0">
      Planner setups
    </Title>
    <Text type="secondary" className="block max-w-4xl">
      Every setup drives the same 1 MW / 2 MWh battery (90% round trip, paid the
      settled imbalance price) with the same planner: every minute, 25 seconds
      in, it re-plans the rest of the running quarter hour and the 24 hours
      after it, with the cycling cost inside the plan, and carries out one
      minute. Only the prices the planner sees differ.
    </Text>
    <Table<SetupDefinitionRow>
      size="small"
      pagination={false}
      scroll={{ x: true }}
      dataSource={setups.map((setup) => ({ ...setup, key: setup.name }))}
      columns={[
        {
          title: "Planner setup",
          dataIndex: "label",
          render: (label: string, row) => (
            <span className="whitespace-nowrap">
              <span style={{ color: setupColor(setups, row.name) }}>■ </span>
              {label}{" "}
              <Tag color={ROLE_TAGS[row.role].color}>
                {ROLE_TAGS[row.role].label}
              </Tag>
            </span>
          ),
        },
        {
          title: "Running quarter hour",
          dataIndex: "running",
          render: (name: string) => priceSource(forecasters, name),
        },
        {
          title: "Later quarter hours (24 h)",
          dataIndex: "future",
          render: (name: string) => priceSource(forecasters, name),
        },
        { title: "Definition", dataIndex: "description" },
      ]}
    />
  </div>
);

const MonthlyValueChart = ({
  groups,
  markers,
  cost,
  setups,
  shown,
}: {
  groups: ValueGroup[];
  markers: VersionMarker[];
  cost: CyclingCost;
  setups: PlannerSetupSpec[];
  shown: string[];
}) => {
  const atCost = groupAt(groups, cost);
  if (atCost.length === 0) {
    return <NoData />;
  }
  // a bar per month and forecaster version, placed in the middle of its days
  const middle = (group: ValueGroup) =>
    dayjs(group.first_day)
      .add(dayjs(group.last_day).diff(group.first_day, "day") / 2, "day")
      .format(API_DAY_FORMAT);
  const data: Data[] = shown.map((setup) => ({
    type: "bar",
    name: labelOf(setups, setup),
    x: atCost.map(middle),
    y: atCost.map((group) => setupIn(group, setup)?.per_mw_year ?? null),
    marker: { color: setupColor(setups, setup) },
    customdata: atCost.map((group) => [
      group.label,
      groupVersionLabel(group),
      group.days,
      euros(setupIn(group, setup)?.net_revenue ?? 0),
    ]),
    hovertemplate:
      "%{customdata[0]} (%{customdata[2]} days, %{customdata[1]})<br>€%{y:,.0f}/MW-year, €%{customdata[3]} net<extra>" +
      labelOf(setups, setup) +
      "</extra>",
  }));
  const { shapes, annotations } = markerShapes(markers);
  const layout: Figure["layout"] = {
    title: {
      text: `Net revenue per month, € per MW-year, at €${cost}/MWh cycling cost`,
    },
    barmode: "group",
    plot_bgcolor: SURFACE_COLOR,
    paper_bgcolor: SURFACE_COLOR,
    xaxis: { type: "date", gridcolor: GRID_COLOR },
    yaxis: { title: { text: "€/MW-year (net)" }, gridcolor: GRID_COLOR },
    shapes,
    annotations,
    legend: { orientation: "h", y: -0.15 },
    margin: { t: 110, r: 20, b: 50, l: 80 },
  };
  return (
    <Plot
      data={data}
      layout={layout}
      useResizeHandler
      style={{ width: "100%", height: 480 }}
      config={plotConfig}
    />
  );
};

const MinuteErrorChart = ({
  rows,
  forecasters,
  metric,
}: {
  rows: MinuteErrorRow[];
  forecasters: ForecasterSpec[];
  metric: ErrorMetric;
}) => {
  if (rows.length === 0) {
    return <NoData />;
  }
  const versions = [
    ...new Set(rows.map((row) => row.per_minute_version)),
  ] as PerMinuteForecasterVersion[];
  const perMinute = forecasters.filter(
    (forecaster) => forecaster.kind === "per_minute",
  );
  const data: Data[] = versions.flatMap((version, versionIndex) =>
    perMinute.flatMap((forecaster): Data[] => {
      const series = rows
        .filter(
          (row) =>
            row.per_minute_version === version &&
            row.forecast === forecaster.name,
        )
        .sort((a, b) => a.minute - b.minute);
      if (series.length === 0) {
        return [];
      }
      const name =
        versions.length > 1
          ? `${forecaster.label} (${PER_MINUTE_VERSION_LABELS[version]})`
          : forecaster.label;
      return [
        {
          type: "scatter",
          mode: "lines+markers",
          name,
          x: series.map((row) => row.minute),
          y: series.map((row) => row[metric]),
          line: {
            color: forecasterColor(forecasters, forecaster.name),
            dash: VERSION_DASHES[versionIndex % VERSION_DASHES.length],
          },
          hovertemplate: `minute %{x}: ${ERROR_METRIC_LABELS[metric]} %{y:.2f} €/MWh<extra>${name}</extra>`,
        },
      ];
    }),
  );
  const layout: Figure["layout"] = {
    title: {
      text: `Per-minute forecasters: ${ERROR_METRIC_LABELS[metric]} by minute into the quarter hour`,
    },
    plot_bgcolor: SURFACE_COLOR,
    paper_bgcolor: SURFACE_COLOR,
    xaxis: {
      title: { text: "Minute into the quarter hour" },
      dtick: 1,
      gridcolor: GRID_COLOR,
    },
    yaxis: {
      title: { text: `${ERROR_METRIC_LABELS[metric]} €/MWh` },
      gridcolor: GRID_COLOR,
      rangemode: "tozero",
    },
    legend: { orientation: "h", y: -0.2 },
    margin: { t: 60, r: 20, b: 50, l: 70 },
  };
  return (
    <Plot
      data={data}
      layout={layout}
      useResizeHandler
      style={{ width: "100%", height: 420 }}
      config={plotConfig}
    />
  );
};

type MinuteErrorTableRow = MinuteErrorRow & { key: string };

const fixed = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? "–" : value.toFixed(digits);

const minuteErrorColumns = (
  forecasters: ForecasterSpec[],
  showVersion: boolean,
): ColumnsType<MinuteErrorTableRow> => [
  ...(showVersion
    ? [
        {
          title: "Version",
          dataIndex: "per_minute_version",
          render: (version: PerMinuteForecasterVersion) =>
            PER_MINUTE_VERSION_LABELS[version],
        },
      ]
    : []),
  { title: "Minute", dataIndex: "minute" },
  {
    title: "Forecaster",
    dataIndex: "forecast",
    render: (name: string) => labelOf(forecasters, name),
  },
  {
    title: "MAE €/MWh",
    dataIndex: "mae",
    align: "right",
    render: (v: number | null) => fixed(v),
  },
  {
    title: "RMSE €/MWh",
    dataIndex: "rmse",
    align: "right",
    render: (v: number | null) => fixed(v),
  },
  {
    title: "Bias €/MWh",
    dataIndex: "bias",
    align: "right",
    render: (v: number | null) => fixed(v),
  },
  {
    title: "Top 5% MAE / bias",
    align: "right",
    render: (_, row) =>
      `${fixed(row.top_5pct_mae)} / ${fixed(row.top_5pct_bias)}`,
  },
  {
    title: "Bottom 5% MAE / bias",
    align: "right",
    render: (_, row) =>
      `${fixed(row.bottom_5pct_mae)} / ${fixed(row.bottom_5pct_bias)}`,
  },
  ...(["above_300", "below_minus_100"] as const).map((spike) => ({
    title: spike === "above_300" ? "> €300 P / R / F1" : "< −€100 P / R / F1",
    render: (_: unknown, row: MinuteErrorTableRow) => {
      const score = row.spikes[spike];
      return score
        ? `${percent(score.precision, 0)} / ${percent(score.recall, 0)} / ${percent(score.f1, 0)}`
        : "–";
    },
  })),
  { title: "n", dataIndex: "n", align: "right" },
];

const MinuteErrorTable = ({
  rows,
  forecasters,
}: {
  rows: MinuteErrorRow[];
  forecasters: ForecasterSpec[];
}) => {
  const showVersion =
    new Set(rows.map((row) => row.per_minute_version)).size > 1;
  return (
    <Table<MinuteErrorTableRow>
      size="small"
      dataSource={rows
        .filter((row) => TABLE_MINUTES.includes(row.minute))
        .map((row) => ({
          ...row,
          key: `${row.per_minute_version}-${row.minute}-${row.forecast}`,
        }))}
      columns={minuteErrorColumns(forecasters, showVersion)}
      pagination={false}
      scroll={{ x: true }}
    />
  );
};

const StepChart = ({
  rows,
  forecasters,
  metric,
}: {
  rows: StepErrorRow[];
  forecasters: ForecasterSpec[];
  metric: ErrorMetric;
}) => {
  const shownRows = rows.filter((row) => row.step <= SHOWN_STEPS);
  if (shownRows.length === 0) {
    return <NoData />;
  }
  const series = [
    ...new Set(shownRows.map((row) => `${row.step_version}|${row.forecaster}`)),
  ];
  const multipleVersions =
    new Set(shownRows.map((row) => row.step_version)).size > 1;
  const metricLabel = ERROR_METRIC_LABELS[metric];
  const dayAheadMetric = metric === "mae" ? "day_ahead_mae" : "day_ahead_rmse";
  const data: Data[] = series.flatMap((key, index): Data[] => {
    const [version, forecaster] = key.split("|");
    const line = shownRows
      .filter(
        (row) => row.step_version === version && row.forecaster === forecaster,
      )
      .sort((a, b) => a.step - b.step);
    const label = multipleVersions
      ? `${labelOf(forecasters, forecaster)} (${STEP_VERSION_LABELS[line[0].step_version]})`
      : labelOf(forecasters, forecaster);
    const color = forecasterColor(forecasters, forecaster);
    const steps = line.map((row) => row.step);
    const traces: Data[] = [
      {
        type: "scatter",
        mode: "lines+markers",
        name: `${label}: sign accuracy`,
        x: steps,
        y: line.map((row) => row.sign_accuracy),
        line: { color },
        hovertemplate: `step %{x}: %{y:.1%}<extra>${label}, sign accuracy</extra>`,
      },
      {
        type: "scatter",
        mode: "lines",
        name: `${label}: sign accuracy when |settled − DA| > €50`,
        x: steps,
        y: line.map((row) => row.large_spread_sign_accuracy),
        line: { color, dash: "dot", width: 1 },
        hovertemplate: `step %{x}: %{y:.1%}<extra>${label}, large spread</extra>`,
      },
      {
        type: "scatter",
        mode: "lines+markers",
        name: `${label}: ${metricLabel}`,
        x: steps,
        y: line.map((row) => row[metric]),
        yaxis: "y2",
        line: { color },
        hovertemplate: `step %{x}: %{y:.1f} €/MWh<extra>${label}, ${metricLabel}</extra>`,
      },
    ];
    if (index === 0) {
      traces.push({
        type: "scatter",
        mode: "lines",
        name: `Day-ahead price: ${metricLabel}`,
        x: steps,
        y: line.map((row) => row[dayAheadMetric]),
        yaxis: "y2",
        line: { color: BASELINE_COLOR, dash: "dash" },
        hovertemplate: `step %{x}: %{y:.1f} €/MWh<extra>day-ahead, ${metricLabel}</extra>`,
      });
    }
    return traces;
  });
  const layout: Figure["layout"] = {
    title: {
      text: "15-minute forecasters by step ahead (step 1 = the quarter hour starting 3 minutes after the forecast)",
    },
    plot_bgcolor: SURFACE_COLOR,
    paper_bgcolor: SURFACE_COLOR,
    xaxis: {
      title: { text: "Step" },
      dtick: 1,
      gridcolor: GRID_COLOR,
      range: [0.5, SHOWN_STEPS + 0.5],
    },
    yaxis: {
      title: { text: "Sign accuracy vs day-ahead" },
      tickformat: ".0%",
      domain: [0.55, 1],
      gridcolor: GRID_COLOR,
    },
    yaxis2: {
      title: { text: `${metricLabel} €/MWh` },
      domain: [0, 0.45],
      gridcolor: GRID_COLOR,
      rangemode: "tozero",
    },
    shapes: [
      {
        type: "rect",
        xref: "x",
        yref: "paper",
        x0: 0.5,
        x1: PROMINENT_STEPS + 0.5,
        y0: 0,
        y1: 1,
        fillcolor: "#fff7e6",
        line: { width: 0 },
        layer: "below",
      },
      {
        type: "line",
        xref: "paper",
        x0: 0,
        x1: 1,
        y0: 0.5,
        y1: 0.5,
        line: { color: NOW_COLOR, width: 1, dash: "dot" },
      },
    ],
    annotations: [
      {
        x: (PROMINENT_STEPS + 1) / 2,
        yref: "paper",
        y: 1,
        text: "next two hours",
        showarrow: false,
        yanchor: "bottom",
        font: { color: NOW_COLOR },
      },
    ],
    legend: { orientation: "h", y: -0.15 },
    margin: { t: 60, r: 20, b: 50, l: 70 },
  };
  return (
    <Plot
      data={data}
      layout={layout}
      useResizeHandler
      style={{ width: "100%", height: 600 }}
      config={plotConfig}
    />
  );
};

type CalibrationTableRow = {
  key: string;
  version: PerMinuteForecasterVersion;
  forecaster: string;
  level: string;
  overall: QuantileCalibrationRow | undefined;
  byMinute: Record<number, QuantileCalibrationRow | undefined>;
};

const calibrationRows = (
  rows: QuantileCalibrationRow[],
): CalibrationTableRow[] => {
  const keys = [
    ...new Set(
      rows.map(
        (row) => `${row.per_minute_version}|${row.forecaster}|${row.level}`,
      ),
    ),
  ];
  return keys.map((key) => {
    const [version, forecaster, level] = key.split("|") as [
      PerMinuteForecasterVersion,
      string,
      string,
    ];
    const matching = rows.filter(
      (row) =>
        row.per_minute_version === version &&
        row.forecaster === forecaster &&
        row.level === level,
    );
    return {
      key,
      version,
      forecaster,
      level,
      overall: matching.find((row) => row.minute === null),
      byMinute: Object.fromEntries(
        TABLE_MINUTES.map((minute) => [
          minute,
          matching.find((row) => row.minute === minute),
        ]),
      ),
    };
  });
};

const calibrationColumns = (
  forecasters: ForecasterSpec[],
): ColumnsType<CalibrationTableRow> => [
  {
    title: "Version",
    dataIndex: "version",
    render: (version: PerMinuteForecasterVersion) =>
      PER_MINUTE_VERSION_LABELS[version],
  },
  {
    title: "Forecaster",
    dataIndex: "forecaster",
    render: (name: string) => labelOf(forecasters, name),
  },
  {
    title: "Quantile",
    dataIndex: "level",
    render: (level: string) => `${Number(level) * 100}%`,
  },
  {
    title: "Settled below it (all minutes)",
    render: (_, row) => (
      <Text
        type={
          row.overall?.share_below !== null &&
          row.overall?.share_below !== undefined &&
          Math.abs(row.overall.share_below - Number(row.level)) > 0.05
            ? "danger"
            : undefined
        }
      >
        {percent(row.overall?.share_below)}
      </Text>
    ),
  },
  ...TABLE_MINUTES.map((minute) => ({
    title: `Minute ${minute}`,
    render: (_: unknown, row: CalibrationTableRow) =>
      percent(row.byMinute[minute]?.share_below),
  })),
  { title: "n", render: (_, row) => row.overall?.n ?? 0 },
];

type ValueWindow = "last_30_days" | "whole_range";

const Report = ({
  report,
  cost,
  metric,
}: {
  report: ImbalanceForecastMetricsReport;
  cost: CyclingCost;
  metric: ErrorMetric;
}) => {
  const [valueWindow, setValueWindow] = useState<ValueWindow>(
    report.source === "research" ? "whole_range" : "last_30_days",
  );
  const [shown, setShown] = useState<string[]>(() =>
    defaultSetups(report.setups),
  );
  const calibration = useMemo(
    () => calibrationRows(report.quantile_calibration),
    [report],
  );
  const groups = groupAt(
    valueWindow === "last_30_days" ? report.headline : report.whole_range,
    cost,
  );
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Title level={5} className="!mb-0">
          Value (decides): a 1 MW / 2 MWh battery, net of €{cost}/MWh cycling
          cost
        </Title>
        <Segmented<ValueWindow>
          value={valueWindow}
          onChange={setValueWindow}
          options={[
            { label: "Last 30 days", value: "last_30_days" },
            { label: "Whole range", value: "whole_range" },
          ]}
        />
      </div>
      <ValueTable groups={groups} setups={report.setups} />
      <SetupDefinitions
        setups={report.setups}
        forecasters={report.forecasters}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Text type="secondary">Setups in the chart:</Text>
        <Select
          mode="multiple"
          className="min-w-96 flex-1"
          value={shown}
          onChange={setShown}
          options={report.setups.map((setup) => ({
            label: setup.label,
            value: setup.name,
          }))}
        />
      </div>
      <MonthlyValueChart
        groups={report.monthly}
        markers={report.markers}
        cost={cost}
        setups={report.setups}
        shown={shown}
      />
      <Title level={5}>Error (explains the value, never replaces it)</Title>
      <MinuteErrorChart
        rows={report.minute_errors}
        forecasters={report.forecasters}
        metric={metric}
      />
      <MinuteErrorTable
        rows={report.minute_errors}
        forecasters={report.forecasters}
      />
      <StepChart
        rows={report.step_errors}
        forecasters={report.forecasters}
        metric={metric}
      />
      <Title level={5}>Quantile calibration of the per-minute forecast</Title>
      <Text type="secondary">
        Share of quarter hours whose settled price fell below each quantile; a
        calibrated forecast sits near the quantile itself. Shares more than 5
        points off are red.
      </Text>
      <Table<CalibrationTableRow>
        size="small"
        dataSource={calibration}
        columns={calibrationColumns(report.forecasters)}
        pagination={false}
      />
    </>
  );
};

export const ImbalanceForecastMetrics = () => {
  const [source, setSource] = useState<MetricsSource>("live");
  const [range, setRange] = useState<[Dayjs, Dayjs]>(DEFAULT_RANGES.live);
  const [cost, setCost] = useState<CyclingCost>(30);
  const [metric, setMetric] = useState<ErrorMetric>("mae");

  const { data, isLoading, error } = useQuery({
    ...getImbalanceForecastMetricsOptions({
      headers: adminAuthHeaders(),
      query: {
        source,
        first_day: range[0].format(API_DAY_FORMAT),
        last_day: range[1].format(API_DAY_FORMAT),
      },
    }),
    retry: false,
    staleTime: 15 * ONE_MINUTE,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <Segmented<MetricsSource>
          value={source}
          onChange={(value) => {
            setSource(value);
            setRange(DEFAULT_RANGES[value]());
          }}
          options={[
            { label: "Live forecasts", value: "live" },
            { label: "Research replay", value: "research" },
          ]}
        />
        <RangePicker
          format={DATE_FORMAT}
          value={range}
          allowClear={false}
          onChange={(value) => {
            if (value?.[0] && value?.[1]) {
              setRange([value[0], value[1]]);
            }
          }}
        />
        <Segmented<CyclingCost>
          value={cost}
          onChange={setCost}
          options={CYCLING_COSTS.map((value) => ({
            label: `€${value}/MWh`,
            value,
          }))}
        />
        <Segmented<ErrorMetric>
          value={metric}
          onChange={setMetric}
          options={[
            { label: "MAE", value: "mae" },
            { label: "RMSE", value: "rmse" },
          ]}
        />
      </div>
      <Text type="secondary" className="block max-w-4xl">
        {source === "live"
          ? "The forecasts production stored, replayed every minute through a battery planner."
          : "The research harness's stored forecasts (validation Jul–Dec 2025, seen test Jan–Sep 2026, final test Sep–Oct 2026), replayed through the same planner and code as the live job."}{" "}
        Every planner is compared with one fed Elia&apos;s live estimate and
        day-ahead prices (the free-data baseline) and one fed the settled prices
        (the ceiling). Value decides, error explains: a change that lowers the
        error and the value is a regression.
      </Text>

      {error && (
        <Alert type="error" message="Could not load the forecast metrics" />
      )}
      {isLoading && (
        <div className="flex min-h-96 items-center justify-center">
          <Spin />
        </div>
      )}
      {data && data.days.length === 0 && <NoData />}
      {data && data.days.length > 0 && (
        <Report
          key={`${data.source}-${data.first_day}-${data.last_day}`}
          report={data}
          cost={cost}
          metric={metric}
        />
      )}
    </div>
  );
};
